/**
 * ChecklistV3 — the 15-item, 3-group go-live checklist for one apartment
 * (COL-529, wizard Step 3). Controlled component: it renders a ChecklistState,
 * emits every edit via `onChange`. It persists no checklist ROWS and calls no
 * API — those writes live in the submit hooks. The one exception is a proof
 * upload: picking a file streams the bytes straight into the private `contracts`
 * bucket (owner-writable under the caller's own folder) and stores the returned
 * path in `fileUrl`; the submit hook ties that path to the unit.
 *
 * `computeChecklistProgress` is the single source of the x/15 tally, the
 * per-group counts and the `blocked` gate — this file never re-derives them.
 * `mode` only changes the per-item requirement chip and the "requested" icon;
 * it does not change the tally (see checklist.ts).
 */
import { useEffect, useState, useRef, type ChangeEvent } from "react";
import {
  CheckCircle2,
  Clock,
  Circle,
  Upload,
  Send,
  Bell,
  ExternalLink,
  Plus,
  Loader2,
  X,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { uploadChecklistDocument, validateImageFile } from "@/lib/v2-upload";
import { openStoredFile } from "@/lib/storage-url";
import {
  DOC_IDS,
  type DocId,
  type ChecklistState,
  type ChecklistItemKey,
  type ChecklistProgress,
  checklistItemDone,
  computeChecklistProgress,
  photoCap,
  shotKeys,
} from "./checklist";

export type ChecklistV3Mode = "owner" | "admin";

export type ChecklistV3Props = {
  mode?: ChecklistV3Mode;
  bedrooms: number;
  /** Room names (Step 2), by position — used to label each bedroom's photo
   *  block with the real room name instead of a generic "Bedroom N". */
  roomTitles?: string[];
  /** false = no KeyNest partner near the building → key handover forced to a person. */
  keynestAvailable?: boolean;
  /** Auto-assigned neutral doorbell name for the unit (display only here). */
  bellName?: string;
  value: ChecklistState;
  onChange: (next: ChecklistState) => void;
  onProgress?: (p: ChecklistProgress) => void;
};

/** Items per group, in display order. */
const GROUP_ITEMS: Record<string, string[]> = {
  docs: [...DOC_IDS],
  object: ["photos", "ware", "washer", "smoke", "key", "bell"],
  contacts: ["contact", "viewing"],
};

const GROUPS = [
  {
    key: "docs",
    labelKey: "admin.checklist.ctl.groupDocs",
    labelFb: "Documents & contracts",
    total: 7,
    tally: "docsDone" as const,
  },
  {
    key: "object",
    labelKey: "admin.checklist.ctl.groupObject",
    labelFb: "Property & furnishing",
    total: 6,
    tally: "objectDone" as const,
  },
  {
    key: "contacts",
    labelKey: "admin.checklist.group.contacts",
    labelFb: "Contacts",
    total: 2,
    tally: "contactsDone" as const,
  },
];

export function ChecklistV3({
  mode = "admin",
  bedrooms,
  roomTitles,
  keynestAvailable = true,
  bellName,
  value,
  onChange,
  onProgress,
}: ChecklistV3Props) {
  const { t } = useLanguage();
  const progress = computeChecklistProgress(value, {
    bedrooms,
    keynestAvailable,
  });

  useEffect(() => {
    onProgress?.(progress);
    // Primitive deps only — `progress` is a fresh object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    progress.done,
    progress.blocked,
    progress.docsDone,
    progress.objectDone,
    progress.contactsDone,
  ]);

  // ponytail: clone the whole checklist on every edit — it is a tiny object,
  // not worth hand-written immutable spreads for 15 nested items.
  const update = (mut: (d: ChecklistState) => void) => {
    const next = structuredClone(value);
    mut(next);
    onChange(next);
  };

  // "use property management for the first viewing" — UI-only, not persisted;
  // flipping it on copies the Hausverwaltung contact into the viewing contact
  // so the item counts as done. ponytail: ephemeral, resets on remount.
  const [viewViaHv, setViewViaHv] = useState(false);

  const pct = Math.round((progress.done / progress.total) * 100);

  return (
    <div className="space-y-4">
      {/* Progress header — numbers come straight from computeChecklistProgress. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm font-medium">
          <span>{t("admin.checklist.heading", "Checklist")}</span>
          <span className="tabular-nums text-muted-foreground">
            {progress.done}/{progress.total}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              progress.blocked ? "bg-destructive" : "bg-primary"
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {GROUPS.map((g) => {
        const done = progress[g.tally];
        const full = done >= g.total;
        const open = value.groups[g.key] !== true; // default open; stored flag = collapsed
        return (
          <Collapsible
            key={g.key}
            open={open}
            onOpenChange={(next) =>
              update((d) => {
                d.groups[g.key] = !next;
              })
            }
            className="rounded-lg border"
          >
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-3 text-left text-sm font-semibold">
              <span>{t(g.labelKey, g.labelFb)}</span>
              <Badge
                variant="outline"
                className={cn(
                  "tabular-nums",
                  full
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {done}/{g.total}
              </Badge>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 border-t p-3">
              {GROUP_ITEMS[g.key].map((id) => (
                <Item
                  key={id}
                  id={id}
                  mode={mode}
                  bedrooms={bedrooms}
                  roomTitles={roomTitles}
                  keynestAvailable={keynestAvailable}
                  bellName={bellName}
                  value={value}
                  update={update}
                  t={t}
                  viewViaHv={viewViaHv}
                  setViewViaHv={setViewViaHv}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-item renderer
// ---------------------------------------------------------------------------

type ItemProps = {
  id: string;
  mode: ChecklistV3Mode;
  bedrooms: number;
  roomTitles?: string[];
  keynestAvailable: boolean;
  bellName?: string;
  value: ChecklistState;
  update: (mut: (d: ChecklistState) => void) => void;
  t: (key: string, fallback?: string) => string;
  viewViaHv: boolean;
  setViewViaHv: (v: boolean) => void;
};

function Item(props: ItemProps) {
  const { id, t } = props;
  return (
    <div className="rounded-md border bg-card p-3 space-y-2">
      <div className="flex items-start gap-2">
        <ItemIcon {...props} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {t(`admin.checklist.item.${id}`, id)}
            </span>
            <RequirementChip {...props} />
          </div>
          <ItemBody {...props} />
          <NoteEditor {...props} />
        </div>
      </div>
    </div>
  );
}

/** Done-state per item, read from the single source of truth. */
function isItemDone(p: ItemProps): boolean {
  const { id, value, bedrooms, keynestAvailable } = p;
  return checklistItemDone(value, { bedrooms, keynestAvailable })[
    id as ChecklistItemKey
  ];
}

function ItemIcon(p: ItemProps) {
  const { id, value } = p;
  const done = isItemDone(p);
  let Icon: LucideIcon = Circle;
  let cls = "text-muted-foreground/40";
  if (done) {
    Icon = CheckCircle2;
    cls = "text-primary";
  } else if (
    (DOC_IDS as readonly string[]).includes(id) &&
    value.docs[id as DocId].status === "requested"
  ) {
    Icon = Clock;
    cls = "text-amber-600";
  }
  return <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", cls)} />;
}

/** owner → required (Pflicht); admin → no chip at all (per Neel). Display only. */
function RequirementChip({ mode, t }: ItemProps) {
  if (mode === "owner") {
    return (
      <Badge
        variant="outline"
        className="border-primary/30 bg-primary/10 text-primary"
      >
        {t("admin.checklist.ctl.required", "Required")}
      </Badge>
    );
  }
  return null;
}

/** Replace successive {{count}} placeholders with the given numbers. */
function fill(str: string, ...nums: number[]): string {
  let i = 0;
  return str.replace(/\{\{count\}\}/g, () =>
    String(nums[i++] ?? nums[nums.length - 1] ?? "")
  );
}

function ItemBody(p: ItemProps) {
  const { id } = p;
  if (id === "wlan") return <Wlan {...p} />;
  if (id === "rundfunk") return <Rundfunk {...p} />;
  if ((DOC_IDS as readonly string[]).includes(id)) return <GenericDoc {...p} />;
  if (id === "photos") return <Photos {...p} />;
  if (id === "ware" || id === "washer") return <Equip {...p} which={id} />;
  if (id === "smoke") return <Smoke {...p} />;
  if (id === "key") return <Keys {...p} />;
  if (id === "bell") return <DoorBell {...p} />;
  if (id === "contact") return <Contacts {...p} />;
  if (id === "viewing") return <Viewing {...p} />;
  return null;
}

// ---- Documents ------------------------------------------------------------

function docButtons(p: ItemProps) {
  const { id, mode, value, update } = p;
  const doc = value.docs[id as DocId];
  const toggle = (status: "requested" | "done", src: "owner" | "coliviq") =>
    update((d) => {
      const cur = d.docs[id as DocId];
      if (cur.status === status) {
        cur.status = "open";
        cur.src = null;
        // Undoing an upload must also clear a prior N/A (gas): otherwise na
        // lingers with status 'open' and the item is stuck not-done at 14/15
        // while the DB still holds not_applicable.
        cur.na = false;
      } else {
        cur.status = status;
        cur.src = src;
        cur.na = false;
      }
    });
  return { doc, toggle, mode };
}

/** Owner-facing per-document upload labels (point 7). */
const OWNER_UPLOAD_LABEL: Record<DocId, { key: string; fb: string }> = {
  wlan: { key: "admin.checklist.ctl.uploadWlan", fb: "Upload WLAN contract" },
  strom: {
    key: "admin.checklist.ctl.uploadStrom",
    fb: "Upload electricity contract",
  },
  gas: { key: "admin.checklist.ctl.uploadGas", fb: "Upload contract" },
  rundfunk: { key: "admin.checklist.ctl.uploadRundfunk", fb: "Upload proof" },
  energie: {
    key: "admin.checklist.ctl.uploadEnergie",
    fb: "Upload energy certificate",
  },
  eigentum: { key: "admin.checklist.ctl.uploadEigentum", fb: "Upload proof" },
  grundriss: {
    key: "admin.checklist.ctl.uploadGrundriss",
    fb: "Upload floor plan",
  },
};

/** PDF + the image types the property-images bucket already accepts. */
const DOC_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/avif";

/**
 * Open an uploaded proof. The stored value is a path in the private `contracts`
 * bucket (or a legacy public URL on old rows), so it can't be a plain href —
 * `openStoredFile` signs the path and opens it synchronously inside the click.
 */
function ViewFileLink({ fileUrl, label }: { fileUrl: string; label: string }) {
  return (
    <button
      type="button"
      onClick={() => void openStoredFile(fileUrl, "contracts")}
      className="inline-flex items-center gap-1 text-xs text-primary underline"
    >
      {label}
      <ExternalLink className="h-3.5 w-3.5" />
    </button>
  );
}

function GenericDoc(p: ItemProps) {
  const { id, t, mode, value, update } = p;
  const docId = id as DocId;
  const doc = value.docs[docId];
  const { toggle } = docButtons(p);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after an error
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const path = await uploadChecklistDocument(file);
      update((d) => {
        const cur = d.docs[docId];
        cur.status = "done";
        cur.src = mode === "owner" ? "owner" : "coliviq";
        cur.na = false;
        cur.fileUrl = path;
      });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  };

  const uploaded = doc.status === "done" && !!doc.fileUrl;
  const uploadLabel = busy
    ? t("admin.checklist.ctl.uploading", "Uploading…")
    : mode === "owner"
      ? t(OWNER_UPLOAD_LABEL[docId].key, OWNER_UPLOAD_LABEL[docId].fb)
      : t("admin.checklist.ctl.uploadForOwner", "Upload");

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={DOC_ACCEPT}
        className="hidden"
        onChange={onPick}
      />
      <div className="flex flex-wrap items-center gap-2">
        {mode === "admin" && (
          <Button
            type="button"
            size="sm"
            variant={doc.status === "requested" ? "secondary" : "outline"}
            onClick={() => toggle("requested", "owner")}
          >
            <Send className="h-4 w-4" />
            {t("admin.checklist.ctl.requestFromOwner", "Request from owner")}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant={uploaded ? "secondary" : "outline"}
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" />
          {uploadLabel}
        </Button>
        {uploaded && (
          <ViewFileLink
            fileUrl={doc.fileUrl!}
            label={t("admin.checklist.ctl.viewFile", "View file")}
          />
        )}
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

function Wlan(p: ItemProps) {
  const { t, value, update } = p;
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <div className="text-xs font-medium">
            {t("admin.checklist.ctl.wlanUser", "Network name")}
          </div>
          <Input
            value={value.wlanUser}
            onChange={(e) =>
              update((d) => {
                d.wlanUser = e.target.value;
              })
            }
          />
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium">
            {t("admin.checklist.ctl.wlanPass", "Password")}
          </div>
          {/* ponytail: wlanPass stays in wizard memory ONLY — the submit hook
              persists the network name + a "password provided" marker, never
              this raw value (no encrypted column yet). Do NOT write it to the DB.
              Shown unmasked (type=text) per Neel — display only, never stored. */}
          <Input
            type="text"
            value={value.wlanPass}
            onChange={(e) =>
              update((d) => {
                d.wlanPass = e.target.value;
              })
            }
          />
        </div>
      </div>
      <GenericDoc {...p} />
    </div>
  );
}

function Rundfunk(p: ItemProps) {
  const { t, mode, value, update } = p;
  const doc = value.docs.rundfunk;
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Inline layout, per the mockup: the contribution-number field and the
  // "Apply now" link are shown together. Clicking the link (it has no
  // redirect/callback back to us) reveals the proof upload — the link alone
  // never fulfils the item; the typed number or the uploaded confirmation does.
  // A tab-focus return after the link reveals the upload too, for owners who
  // applied and came back.
  const [applied, setApplied] = useState(doc.rb === "applied");
  const awaiting = useRef(false);
  useEffect(() => {
    const onFocus = () => {
      if (!awaiting.current) return;
      awaiting.current = false;
      setApplied(true);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // A typed number fulfils the item; clearing it reopens — unless an uploaded
  // confirmation already fulfils it (keep rb "applied" so done-ness holds).
  const onNumber = (next: string) =>
    update((d) => {
      d.rbNumber = next;
      const cur = d.docs.rundfunk;
      cur.rb = next.trim() ? "number" : cur.fileUrl ? "applied" : null;
    });

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after an error
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const path = await uploadChecklistDocument(file);
      update((d) => {
        const cur = d.docs.rundfunk;
        cur.rb = "applied";
        cur.fileUrl = path;
        cur.src = mode === "owner" ? "owner" : "coliviq";
      });
      setApplied(true);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  };

  const uploaded = doc.rb === "applied" && !!doc.fileUrl;
  const showUpload = applied || uploaded;

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={DOC_ACCEPT}
        className="hidden"
        onChange={onPick}
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <div className="text-xs font-medium">
            {t("admin.checklist.ctl.rbNumber", "Contribution number")}
          </div>
          <Input
            value={value.rbNumber}
            inputMode="numeric"
            placeholder="123 456 789"
            className="w-56"
            onChange={(e) => onNumber(e.target.value)}
          />
        </div>
        <a
          href="https://www.rundfunkbeitrag.de/buergerinnen-und-buerger/formulare/anmelden#step_personendaten"
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            awaiting.current = true;
            setApplied(true);
          }}
          className="inline-flex h-9 items-center gap-1 text-sm text-primary underline"
        >
          {t("admin.checklist.ctl.rbApply", "Apply now")}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        {showUpload && (
          <Button
            type="button"
            size="sm"
            variant={uploaded ? "secondary" : "outline"}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            {busy
              ? t("admin.checklist.ctl.uploading", "Uploading…")
              : t(
                  "admin.checklist.ctl.rbProofUpload",
                  "Upload application confirmation"
                )}
          </Button>
        )}
        {uploaded && (
          <ViewFileLink
            fileUrl={doc.fileUrl!}
            label={t("admin.checklist.ctl.viewFile", "View file")}
          />
        )}
      </div>
      {applied && !uploaded && (
        <p className="text-xs text-primary">
          {t(
            "admin.checklist.ctl.rbReturnHint",
            "Zurück von der Anmeldung? Laden Sie jetzt Ihre Bestätigung hoch."
          )}
        </p>
      )}
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

// ---- The unit -------------------------------------------------------------

function Photos(p: ItemProps) {
  const { t, bedrooms, roomTitles, value, update } = p;
  const keys = shotKeys(bedrooms);
  const corners = [
    t("admin.checklist.ctl.corner0", "Door left"),
    t("admin.checklist.ctl.corner1", "Back left"),
    t("admin.checklist.ctl.corner2", "Back right"),
    t("admin.checklist.ctl.corner3", "Door right"),
  ];
  const exterior = [
    t("admin.checklist.ctl.extSlot0", "Front"),
    t("admin.checklist.ctl.extSlot1", "Entrance"),
  ];

  const inputRef = useRef<HTMLInputElement>(null);
  const activeSlot = useRef<string | null>(null);
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const recount = (d: ChecklistState, k: string) => {
    d.shots[k] = Array.from(
      { length: photoCap(k) },
      (_, j) => !!d.pics[`${k}:${j}`]
    ).filter(Boolean).length;
  };

  const pickSlot = (slot: string) => {
    activeSlot.current = slot;
    setErr(null);
    inputRef.current?.click();
  };

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const slot = activeSlot.current;
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after an error
    if (!slot || !file) return;
    const check = validateImageFile(file);
    if (!check.ok) {
      setErr(check.reason ?? "Invalid image");
      return;
    }
    setBusySlot(slot);
    setErr(null);
    try {
      const path = await uploadChecklistDocument(file);
      update((d) => {
        d.pics[slot] = true;
        d.picPaths[slot] = path;
        recount(d, slot.split(":")[0]);
      });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusySlot(null);
    }
  };

  const removeSlot = (slot: string) =>
    update((d) => {
      delete d.pics[slot];
      delete d.picPaths[slot];
      recount(d, slot.split(":")[0]);
    });

  const label = (k: string) => {
    if (k === "outside")
      return t("admin.checklist.ctl.outsideLabel", "Building exterior");
    if (k === "common")
      return t("admin.checklist.ctl.commonLabel", "Kitchen & living area");
    // Prefer the real Step-2 room name for this bedroom slot (r1 → room 1);
    // fall back to the generic "Bedroom N" when it hasn't been named.
    const named = roomTitles?.[Number(k.slice(1)) - 1]?.trim();
    return (
      named || `${t("admin.checklist.ctl.roomLabel", "Bedroom")} ${k.slice(1)}`
    );
  };
  const slotName = (k: string, i: number) =>
    k === "outside"
      ? (exterior[i] ?? `#${i + 1}`)
      : (corners[i] ?? `#${i + 1}`);

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        className="hidden"
        onChange={onPick}
      />
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.checklist.ctl.photoHint",
          "All 4 corner photos for each bedroom and the common area, plus 2 of the building exterior."
        )}
      </p>
      {/* ponytail: v3 drops the 360° video slot entirely — not rendered. */}
      {keys.map((k) => (
        <div key={k} className="space-y-1">
          <div className="text-xs font-medium">{label(k)}</div>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: photoCap(k) }, (_, i) => {
              const slot = `${k}:${i}`;
              const on = !!value.picPaths[slot];
              const busy = busySlot === slot;
              return (
                <span
                  key={i}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors",
                    on
                      ? "border-primary bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => pickSlot(slot)}
                    className="inline-flex items-center gap-1"
                  >
                    {busy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : on ? (
                      "✓ "
                    ) : (
                      "+ "
                    )}
                    {slotName(k, i)}
                  </button>
                  {on && !busy && (
                    <button
                      type="button"
                      aria-label={t(
                        "admin.checklist.ctl.removePhoto",
                        "Remove"
                      )}
                      onClick={() => removeSlot(slot)}
                      className="ml-0.5 opacity-70 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      ))}
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

function Equip(p: ItemProps & { which: "ware" | "washer" }) {
  const { t, which, value, update } = p;
  const e = value.equip[which];
  const hint = which === "ware" ? "wareHint" : "washerHint";

  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onPick = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // allow re-picking the same file after an error
    if (!file) return;
    const check = validateImageFile(file);
    if (!check.ok) {
      setErr(check.reason ?? "Invalid image");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const path = await uploadChecklistDocument(file);
      update((d) => {
        d.equip[which].photo = true;
        d.equip[which].photoPath = path;
      });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = () =>
    update((d) => {
      d.equip[which].photo = false;
      d.equip[which].photoPath = "";
    });

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        className="hidden"
        onChange={onPick}
      />
      <p className="text-xs text-muted-foreground">
        {t(`admin.checklist.ctl.${hint}`, "")}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={e.ok === true ? "secondary" : "outline"}
          onClick={() =>
            update((d) => {
              d.equip[which].ok = e.ok === true ? null : true;
            })
          }
        >
          {t("admin.checklist.ctl.yes", "Yes")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={e.ok === false ? "secondary" : "outline"}
          onClick={() =>
            update((d) => {
              d.equip[which].ok = e.ok === false ? null : false;
            })
          }
        >
          {t("admin.checklist.ctl.no", "No")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={e.photo ? "secondary" : "outline"}
          disabled={busy}
          onClick={() => {
            setErr(null);
            inputRef.current?.click();
          }}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {e.photo
            ? t("admin.checklist.ctl.photoDone", "Photo ✓")
            : t("admin.checklist.ctl.photo", "Upload photo")}
        </Button>
        {e.photo && !busy && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={removePhoto}
            aria-label={t("admin.checklist.ctl.removePhoto", "Remove")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

function Smoke(p: ItemProps) {
  const { t, mode, value, update } = p;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.checklist.ctl.smokeHint",
          "In every bedroom, living room and the hallway."
        )}
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={value.smoke === "yes" ? "secondary" : "outline"}
          onClick={() =>
            update((d) => {
              d.smoke = "yes";
            })
          }
        >
          {t("admin.checklist.ctl.yes", "Yes")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={value.smoke === "no" ? "destructive" : "outline"}
          onClick={() =>
            update((d) => {
              d.smoke = "no";
            })
          }
        >
          {t("admin.checklist.ctl.no", "No")}
        </Button>
      </div>
      {value.smoke === "no" && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          <div className="font-semibold">
            {t(
              "admin.checklist.ctl.smokeBlockTitle",
              "Without smoke detectors the apartment cannot be created."
            )}
          </div>
          {mode === "owner" && (
            <div className="mt-1 text-xs">
              {t(
                "admin.checklist.ctl.smokeBlockHint",
                "Please install smoke detectors and then select “Yes”."
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Keys(p: ItemProps) {
  const { t, bedrooms, keynestAvailable, value, update } = p;
  const nestOk = keynestAvailable !== false;
  const selected = nestOk ? (value.key ?? "") : "person";
  const n = Math.max(1, bedrooms);
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {fill(
          t(
            "admin.checklist.ctl.keysNeeded",
            "Needed: {{count}}× apartment key, {{count}}× room key, 1× mailbox key"
          ),
          1,
          n
        )}
      </p>
      <RadioGroup
        value={selected}
        onValueChange={(v) =>
          update((d) => {
            d.key = v as ChecklistState["key"];
          })
        }
        className="space-y-1"
      >
        <label
          className={cn(
            "flex items-center gap-2 text-sm",
            !nestOk && "opacity-50"
          )}
        >
          <RadioGroupItem value="keynest" disabled={!nestOk} />
          <span>{t("admin.checklist.ctl.keyNest", "KeyNest partner")}</span>
          <span className="text-xs text-muted-foreground">
            {nestOk
              ? t("admin.checklist.ctl.keyNestOk", "KeyNest partner nearby")
              : t(
                  "admin.checklist.ctl.keyNestNo",
                  "No KeyNest partner within 15 km"
                )}
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <RadioGroupItem value="person" />
          <span>
            {t("admin.checklist.ctl.keyPerson", "In-person handover")}
          </span>
          {!nestOk && (
            <Badge
              variant="outline"
              className="border-primary/30 bg-primary/10 text-primary"
            >
              {t("admin.checklist.ctl.required", "Required")}
            </Badge>
          )}
        </label>
      </RadioGroup>
      {selected === "person" && (
        <PersonFields
          {...p}
          who="key"
          showEmail
          label={t(
            "admin.checklist.ctl.keyWho",
            "Who handles the key handovers?"
          )}
        />
      )}
    </div>
  );
}

function DoorBell(p: ItemProps) {
  const { t, bellName } = p;
  // The doorbell name is auto-assigned and stored on apartments.doorbell_name,
  // not in ChecklistState — display only here (progress always counts it done).
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm">
        <Bell className="h-4 w-4 text-muted-foreground" />
        <span className="font-mono">{bellName || "CLVIQ-EG-Links"}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.checklist.ctl.bellHint",
          "Neutral coliviq name, assigned automatically at onboarding."
        )}
      </p>
    </div>
  );
}

// ---- Contacts -------------------------------------------------------------

function PersonFields(
  p: ItemProps & {
    who: "hv" | "hm" | "key" | "view";
    showEmail?: boolean;
    label?: string;
  }
) {
  const { t, who, showEmail, label, value, update } = p;
  const person = value.people[who];
  return (
    <div className="space-y-2">
      {label && <div className="text-xs font-medium">{label}</div>}
      <div
        className={cn(
          "grid gap-2",
          showEmail ? "sm:grid-cols-3" : "sm:grid-cols-2"
        )}
      >
        <Input
          value={person.name}
          placeholder={t("admin.checklist.ctl.contactName", "Name / company")}
          onChange={(e) =>
            update((d) => {
              d.people[who].name = e.target.value;
            })
          }
        />
        <Input
          value={person.phone}
          placeholder={t("admin.checklist.ctl.contactPhone", "Phone")}
          onChange={(e) =>
            update((d) => {
              d.people[who].phone = e.target.value;
            })
          }
        />
        {showEmail && (
          <Input
            value={person.email}
            placeholder={t("admin.checklist.ctl.contactEmail", "Email")}
            onChange={(e) =>
              update((d) => {
                d.people[who].email = e.target.value;
              })
            }
          />
        )}
      </div>
    </div>
  );
}

function Contacts(p: ItemProps) {
  const { t } = p;
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.checklist.ctl.contactMin",
          "Please provide at least one contact."
        )}
      </p>
      <PersonFields
        {...p}
        who="hv"
        showEmail
        label={t("admin.checklist.ctl.roleHv", "Management")}
      />
      <PersonFields
        {...p}
        who="hm"
        showEmail
        label={t("admin.checklist.ctl.roleHm", "Caretaker")}
      />
    </div>
  );
}

function Viewing(p: ItemProps) {
  const { t, value, update, viewViaHv, setViewViaHv } = p;
  const allowed = value.viewing.allowed;
  const sel = allowed === true ? "yes" : allowed === false ? "no" : "";
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.checklist.ctl.viewHint",
          "For the first viewing, someone on site can open the door."
        )}
      </p>
      <RadioGroup
        value={sel}
        onValueChange={(v) =>
          update((d) => {
            d.viewing.allowed = v === "yes";
          })
        }
        className="space-y-1"
      >
        <label className="flex items-center gap-2 text-sm">
          <RadioGroupItem value="yes" />
          {t("admin.checklist.ctl.viewYes", "Yes, viewing possible")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <RadioGroupItem value="no" />
          {t("admin.checklist.ctl.viewNo", "No viewing")}
        </label>
      </RadioGroup>

      {allowed === true && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={viewViaHv}
              onCheckedChange={(on) => {
                setViewViaHv(on);
                if (on)
                  update((d) => {
                    d.people.view = { ...d.people.hv };
                  });
              }}
            />
            {t(
              "admin.checklist.ctl.viewUseHv",
              "Property management / caretaker opens the door"
            )}
          </label>
          {viewViaHv ? (
            <p className="text-xs text-muted-foreground">
              {t(
                "admin.checklist.ctl.viewUseHvOn",
                "We will contact the person named above."
              )}
            </p>
          ) : (
            <PersonFields
              {...p}
              who="view"
              showEmail
              label={t(
                "admin.checklist.ctl.viewWho",
                "Who opens the door for the first viewing?"
              )}
            />
          )}
        </div>
      )}

      {allowed === false && (
        // Soft warning only — never blocks go-live.
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <div className="font-semibold">
            {t(
              "admin.checklist.ctl.viewWarnTitle",
              "We recommend allowing a first viewing."
            )}
          </div>
          <div className="mt-1 text-xs">
            {t(
              "admin.checklist.ctl.viewWarnBody",
              "Tenants are more likely to rent an apartment they have seen in person."
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Shared note editor ---------------------------------------------------

function NoteEditor(p: ItemProps) {
  const { id, mode, t, value, update } = p;
  // bell has no note affordance (always done, nothing to explain).
  if (id === "bell") return null;
  const open = !!value.noteOpen[id];
  const note = value.notes[id] || "";
  if (!open && !note) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        onClick={() =>
          update((d) => {
            d.noteOpen[id] = true;
          })
        }
      >
        <Plus className="h-3.5 w-3.5" />
        {t("admin.checklist.ctl.addNote", "Add note")}
      </button>
    );
  }
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium">
        {mode === "owner"
          ? t("admin.checklist.ctl.noteLabelOwner", "Note to coliviq")
          : t("admin.checklist.ctl.noteLabelAdmin", "Owner")}
      </div>
      <Textarea
        value={note}
        placeholder={t(
          "admin.checklist.ctl.notePh",
          "e.g. floor plan only exists on paper …"
        )}
        onChange={(e) =>
          update((d) => {
            d.notes[id] = e.target.value;
          })
        }
      />
      {note.trim() && (
        <p className="text-xs text-muted-foreground">
          {t(
            "admin.checklist.ctl.noteCovers",
            "This note is saved with the item."
          )}
        </p>
      )}
    </div>
  );
}
